import {
  Component,
  computed,
  Input,
  OnInit,
  Signal,
  ViewChild,
} from '@angular/core';
import {
  ActionSheetController,
  IonDatetime,
  ModalController,
  Platform,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { catchError, EMPTY, finalize, map, repeat } from 'rxjs';
import {
  AttendeeInformation,
  BookingTypeValue,
  CalendarEvent,
  DATETIMEFORMART,
  EventRequest,
  Place,
  reminderMinutes,
  SearchRequest,
  showAsOptions,
  UnleashFeatureFlags,
} from 'src/app/interfaces/interfaces';
import { ApiService } from 'src/app/services/api-services/api.service';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';
import { PlacesService } from 'src/app/services/placesService/places.service';
import { LocalNotificationService } from '../../services/localNotification/local-notification.service';
import { Router } from '@angular/router';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { EVENTPAGETYPES } from '../place/place.component';
import { StringInfoMessageService } from 'src/app/services/string-info-message/string-info-message.service';
import { HapticsService } from 'src/app/services/haptics-service/haptics.service';
import { UserStore } from 'src/app/stores/user.store';
import { PlacesStore } from 'src/app/stores/places.store';
import { BookingsStore } from 'src/app/stores/bookings.store';
import { AddAttendeeComponent } from '../add-attendee/add-attendee.component';
import { UnleashService } from 'src/app/services/unleash/unleash.service';
import {
  getCheckoutDelegates,
  isCheckoutDelegationConfigured,
  reconcileParticipantSelection,
  stripCheckoutDelegateUiState,
} from 'src/app/utils/checkout-delegates';

type PurposeType = 'edit' | 'book' | 'plannedBook' | 'workingHours' | 'readOnlyBooking' | 'slotBook';

@Component({
  selector: 'app-event',
  templateUrl: './event.component.html',
  styleUrls: ['./event.component.scss'],
})
export class EventComponent implements OnInit {
  @Input() public place: Place;
  @Input() public purpose: PurposeType;
  @Input() public meetingToEdit: CalendarEvent;
  @Input() public slotStartTime: string;
  @Input() public slotEndTime: string;
  @Input() public slotMaxEndTime: string;
  @Input() public isSlotBetweenBusySlots: boolean;
  @ViewChild('durationPicker') durationPicker!: IonDatetime;
  durationPickerVisible = false;
  weekdays: {
    value: string;
    active: boolean;
    stringName: string;
    index: number;
  }[] = [
    { value: 'Sun', active: false, stringName: 'weekdayName.Sun', index: 0 },
    { value: 'Mon', active: false, stringName: 'weekdayName.Mon', index: 1 },
    { value: 'Tue', active: false, stringName: 'weekdayName.Tue', index: 2 },
    { value: 'Wed', active: false, stringName: 'weekdayName.Wed', index: 3 },
    { value: 'Thu', active: false, stringName: 'weekdayName.Thu', index: 4 },
    { value: 'Fri', active: false, stringName: 'weekdayName.Fri', index: 5 },
    { value: 'Sat', active: false, stringName: 'weekdayName.Sat', index: 6 },
  ];
  disableAllDayToggle = false;
  disableOnlineMeetingToggle = false;
  modalTitle: string = '';
  errorMessage;
  showError;
  showAsOptions = showAsOptions;
  repeatEveryOptions = []
    .constructor(30)
    .fill(0)
    .map((_, index) => index + 1);
  reminderOptions = reminderMinutes;
  maxAllowedDuration = { hours: [], minutes: [] };
  vehicleNumber;
  locale = this.dateTimeFormatService.selectedDateTimeFormat;
  duration;
  seriesMessage;
  resourceMapUrl: string = '';
  unleashFeatureFlags = UnleashFeatureFlags;
  responseLoading: boolean = false;
  selectedResponse: 'accept' | 'tentativelyAccept' | 'decline' | null = null;
  scheduleMeetingInputValue = {
    localStartTimeValue: moment()
      .add(5 - (moment().minute() % 5), 'minutes')
      .format(DATETIMEFORMART),
    localEndTimeValue: moment()
      .add(5 - (moment().minute() % 5) + 30, 'minutes')
      .format(DATETIMEFORMART),
    isAllDay: false,
    recurrenceStartDate: moment().format(DATETIMEFORMART),
    recurrenceEndDate: moment().format(DATETIMEFORMART),
    recurrenceType: 'none',
    repeatEvery: 1,
    selectedWeekDays: [],
    subject: '',
    showAs: undefined,
    reminder: 15,
    attendees: [] as AttendeeInformation[],
    isOnlineMeeting: false,
  };
  bookingLoading: boolean;
  constructor(
    private modalController: ModalController,
    private dateTimeFormatService: DatetimeFormatService,
    public apiService: ApiService,
    private translateService: TranslateService,
    private placesService: PlacesService,
    private localNotificationService: LocalNotificationService,
    private platform: Platform,
    private router: Router,
    private toastService: ToastService,
    private stringInfoMessageService: StringInfoMessageService,
    private hapticsService: HapticsService,
    public userStore: UserStore,
    private placesStore: PlacesStore,
    public unleashService: UnleashService,
    private bookingsStore: BookingsStore,
    private actionSheetController: ActionSheetController,
  ) {}

  ngOnInit() {
    this.initValues(this.purpose);
  }

  get checkoutDelegates(): AttendeeInformation[] {
    return getCheckoutDelegates(this.scheduleMeetingInputValue.attendees);
  }

  get showCheckOutDelegateEditor(): boolean {
    return (
      this.purpose !== 'readOnlyBooking' && isCheckoutDelegationConfigured(this.place)
    );
  }

  get showCheckOutDelegateDetails(): boolean {
    return (this.checkoutDelegates.length > 0
    );
  }

  get checkoutDelegateSummary(): string {
    return this.checkoutDelegates
      .map((delegate) => {
        const name = [delegate.firstName, delegate.lastName]
          .filter(Boolean)
          .join(' ')
          .trim();
        return name || delegate.userPrincipalName;
      })
      .join(', ');
  }

  get showParticipantsPicker(): boolean {
    return (
      this.place?.type === 'room' &&
      this.purpose !== 'readOnlyBooking' &&
      this.place?.tag !== 'Parking' &&
      (this.purpose !== 'book' || this.showCheckOutDelegateEditor)
    );
  }

  initValues(purpose: PurposeType) {
    //COMMON DATA : VehicleNumber - ShowAs - Locale - maxDurationHourMinutes arrays - Subject(not for edit)
    // duration will be calculated after switch case at the end
    this.generateCommonData();
    switch (purpose) {
      case 'book':
        this.modalTitle = EVENTPAGETYPES.quickBooking;
        // this.generateSubject();
        break;

      case 'workingHours':
        this.modalTitle = EVENTPAGETYPES.workingHours;
        this.scheduleMeetingInputValue.localStartTimeValue = moment()
          .set('hours', this.userStore.user().workingHours.startTime.hour)
          .set('minutes', this.userStore.user().workingHours.startTime.minute)
          .format(DATETIMEFORMART);
        this.scheduleMeetingInputValue.localEndTimeValue = moment()
          .set('hours', this.userStore.user().workingHours.endTime.hour)
          .set('minutes', this.userStore.user().workingHours.endTime.minute)
          .format(DATETIMEFORMART);
        this.scheduleMeetingInputValue.isAllDay = false;
        this.disableAllDayToggle = true;
        break;

      case 'plannedBook':
        this.modalTitle = EVENTPAGETYPES.scheduledBooking;

        this.generateSubject();
        this.scheduleMeetingInputValue.localStartTimeValue = moment(
          this.placesStore.searchRequest().startTime,
        ).format(DATETIMEFORMART);
        this.scheduleMeetingInputValue.localEndTimeValue = moment(
          this.placesStore.searchRequest().endTime,
        ).format(DATETIMEFORMART);
        // if Allday is toggled from search request we disable all day in modal
        this.scheduleMeetingInputValue.isAllDay =
          this.placesStore.searchRequest().isAllDay;
        if (this.scheduleMeetingInputValue.isAllDay)
          this.disableAllDayToggle = true;
        if (this.placesStore.searchRequestType() == 'series') {
          this.scheduleMeetingInputValue.recurrenceType =
            this.placesStore.searchRequest().recurrencePattern?.accuranceType;
          this.scheduleMeetingInputValue.repeatEvery =
            this.placesStore.searchRequest().recurrencePattern?.interval;
          this.scheduleMeetingInputValue.recurrenceStartDate = moment(
            this.placesStore.searchRequest().recurrencePattern?.startDate,
          ).format(DATETIMEFORMART);
          this.scheduleMeetingInputValue.recurrenceEndDate = moment(
            this.placesStore.searchRequest().recurrencePattern?.endDate,
          ).format(DATETIMEFORMART);
          this.scheduleMeetingInputValue.selectedWeekDays =
            this.placesStore.searchRequest().recurrencePattern?.accuranceDays;
          this.generateAndUpdateSeriesMessage();
        }
        break;

      case 'edit':
        this.modalTitle = EVENTPAGETYPES.editBooking;
        this.initValuesForEditAndReadOnlyCase();
        break;
      case 'readOnlyBooking':
        this.modalTitle = EVENTPAGETYPES.readOnlyBooking;
        this.initValuesForEditAndReadOnlyCase();
        break;
      case 'slotBook':
        this.modalTitle = EVENTPAGETYPES.slotBooking;
        this.generateSubject();
        this.scheduleMeetingInputValue.localStartTimeValue = this.slotStartTime;
        this.scheduleMeetingInputValue.localEndTimeValue = this.slotEndTime;
        this.scheduleMeetingInputValue.isAllDay = false;
        this.disableAllDayToggle = true;
        this.calculateDateTimeHours(
          this.scheduleMeetingInputValue.localStartTimeValue,
          this.slotMaxEndTime
        );
        this.calculateDateTimeMinutes(
          this.scheduleMeetingInputValue.localStartTimeValue,
          this.slotMaxEndTime
        );
        break;
    }
    this.generateDuration();
  }

  private generateSubject() {
    if (this.purpose == 'edit') return;
    if (this.place?.tag == 'Parking' && this.vehicleNumber != undefined) {
      this.scheduleMeetingInputValue.subject =
        this.translateService.instant('vehicleNumberSubject') +
        ' ' +
        this.vehicleNumber;
    }
  }

  private generateAndUpdateSeriesMessage(showDaysNames = true) {
    this.seriesMessage =
      this.stringInfoMessageService.seriesEventInformationMessageForSeriesSearchRequest(
        this.scheduleMeetingInputValue,
        showDaysNames,
      );
  }

  private generateCommonData() {
    const userInformation = this.userStore.user();
    this.vehicleNumber = userInformation?.vehicleNumber;

    const userPreferences = this.userStore.userPreferences();
    this.scheduleMeetingInputValue.showAs =
      userPreferences?.userSettings.bookingStatus;

    this.generateSubject();
    this.calculateDateTimeHours(
      this.scheduleMeetingInputValue.localStartTimeValue,
    );
    this.calculateDateTimeMinutes(
      this.scheduleMeetingInputValue.localStartTimeValue,
    );
  }

  private generateDuration() {
    this.calculateDuration(
      this.scheduleMeetingInputValue.localStartTimeValue,
      this.scheduleMeetingInputValue.localEndTimeValue,
    );
    this.normalizeDuration();
  }

  // 1. Calculates the absolute duration without dropping day/hour values
  public calculateDuration(startTime: string, endTime: string) {
    const duration = moment.duration(moment(endTime).diff(moment(startTime)));

    // Use asHours() to get total absolute hours across day boundaries
    const totalHours = Math.floor(duration.asHours());
    const hours = totalHours.toString().padStart(2, '0');
    const min = duration.minutes().toString().padStart(2, '0');
    this.duration = hours + ':' + min;
    this.normalizeDuration();
  }

  // 2. Normalizes the duration text format and enforces safety caps
  private normalizeDuration() {
    try {
      const [hStr, mStr] = (this.duration || '00:00').split(':');
      let h = parseInt(hStr, 10);
      let m = parseInt(mStr, 10);
      if (isNaN(h) || isNaN(m)) {
        h = 0;
        m = 0;
      }

      // CRITICAL FIX: Bypass standard caps if it's a perfect 24-hour All-Day event
      if (h === 24 && m === 0) {
        this.duration = '24:00';
        return;
      }

      const hours = this.maxAllowedDuration.hours || [];
      if (!hours.length) {
        this.maxAllowedDuration.minutes = Array.from(
          { length: 60 },
          (_, i) => i,
        );
        return;
      }

      const lastHour = hours[hours.length - 1];
      if (h > lastHour) {
        h = lastHour;
      }

      if (h === lastHour) {
        this.calculateDateTimeMinutes(
          this.scheduleMeetingInputValue.localStartTimeValue,
        );
        const maxMin = this.maxAllowedDuration.minutes.length
          ? this.maxAllowedDuration.minutes[
              this.maxAllowedDuration.minutes.length - 1
            ]
          : 0;
        if (m > maxMin) {
          m = maxMin;
        }
      } else {
        this.maxAllowedDuration.minutes = Array.from(
          { length: 60 },
          (_, i) => i,
        );
        if (m > 59) m = 59;
      }

      this.duration = `${String(h).padStart(2, '0')}:${String(m).padStart(
        2,
        '0',
      )}`;
    } catch {}
  }
  doBookNewEvent() {
    this.bookingLoading = true;
    this.errorMessage = '';
    this.showError = false;
    if (!this.validateInput()) return (this.bookingLoading = false);

    let eventRequest: EventRequest = this.mapEventRequest();
    this.bookingsStore
      .makeNewBooking(eventRequest)
      .pipe(
        catchError((ex) => {
          console.log(ex);
          if (this.purpose == 'slotBook') {
            if (ex.error == 'PlaceNotAvailable') {
              this.errorMessage = 'resourceAvailability.slotNoLongerAvailable';
              this.showError = true;
            } else {
              this.errorMessage = 'resourceAvailability.bookingFailed';
              this.showError = true;
            }
          } else {
            if (ex.error == 'PlaceNotAvailable') {
              // TODO present place not available
              this.errorMessage = 'Place was not available at the selected time.';
              this.showError = true;
            } else if (ex.error?.error == 'LicenseNotFound') {
            } else if (ex.error?.error == 'LicenseExpired') {
            } else if (ex.error?.message == 'errors.occurrenceCrossingBoundary') {
              this.errorMessage = ex.error?.message;
              this.showError = true;
            } else if (ex.error?.message) {
              this.createEventGeneralErrorHandler(ex.error?.message);
            } else {
              this.createEventGeneralErrorHandler();
            }
          }
          return EMPTY;
        }),
        finalize(() => {
          this.bookingLoading = false;
        }),
      )
      .subscribe((res) => {
        if (this.purpose == 'slotBook') {
          this.modalController.dismiss({ bookingCreated: true }, 'bookingSuccess');
          this.presentNotification(eventRequest.startTime, eventRequest.endTime);
        } else {
          this.dismissModal();
          this.presentNotification(eventRequest.startTime, eventRequest.endTime);
        }
      });
  }

  doEditBooking() {
    this.bookingLoading = true;
    if (!this.validateInput()) return (this.bookingLoading = false);

    const eventRequest: EventRequest = this.mapEventRequest();
    this.editBooking(eventRequest);
  }

  private editBooking(eventRequest: EventRequest) {
    let eventId =
      this.meetingToEdit.editMasterEvent == true
        ? this.meetingToEdit.seriesMasterId
        : this.meetingToEdit.id;
    return this.bookingsStore
      .editBooking(eventId, this.meetingToEdit.editMasterEvent, eventRequest)
      .pipe(
        catchError((ex) => {
          console.log(ex);
          if (ex.error == 'PlaceNotAvailable') {
            // TODO present place not available
            this.errorMessage = 'Place was not available at the selected time.';
            this.showError = true;
          } else if (ex.error?.message == 'errors.occurrenceCrossingBoundary') {
            this.errorMessage = ex.error?.message;
            this.showError = true;
          } else if (ex.error?.message) {
            this.createEventGeneralErrorHandler(ex.error?.message);
          } else {
            this.createEventGeneralErrorHandler();
          }
          return ex;
        }),
        finalize(() => {
          this.bookingLoading = false;
        }),
      )
      .subscribe((res) => {
        this.dismissModal();
        // this.changeSeriesActionIsLoading(eventId, true);
      });
  }

  handleOccurenceType() {
    this.updateValuesBasedOnRecurrenceType(
      this.scheduleMeetingInputValue.recurrenceType,
    );
  }

  updateValuesBasedOnRecurrenceType(recurrenceType) {
    switch (recurrenceType) {
      case 'Daily':
        this.scheduleMeetingInputValue.recurrenceEndDate = moment(
          this.scheduleMeetingInputValue.localStartTimeValue,
        )
          .add(2, 'days')
          .format(DATETIMEFORMART);
        break;
      case 'Weekly':
        this.scheduleMeetingInputValue.recurrenceEndDate = moment(
          this.scheduleMeetingInputValue.localStartTimeValue,
        )
          .add(8, 'days')
          .format(DATETIMEFORMART);
        this.weekdays.forEach((day) => {
          if (moment().format('ddd') == day.value) {
            day.active = true;
          } else {
            day.active = false;
          }
        });
        break;
      case 'AbsoluteMonthly':
        this.scheduleMeetingInputValue.recurrenceEndDate = moment(
          this.scheduleMeetingInputValue.localStartTimeValue,
        )
          .add(31, 'days')
          .format(DATETIMEFORMART);
        break;
      default:
        this.scheduleMeetingInputValue.recurrenceEndDate = moment(
          this.scheduleMeetingInputValue.localStartTimeValue,
        ).format(DATETIMEFORMART);
        break;
    }
    this.generateAndUpdateSeriesMessage(false);
  }

  mapEventRequest(): EventRequest {
    let bookingType : BookingTypeValue;
    if(this.purpose == 'book') bookingType = BookingTypeValue.QuickBooking;
    if(this.purpose == 'plannedBook') bookingType = BookingTypeValue.FilterBooking;
    if(this.purpose == 'workingHours') bookingType = BookingTypeValue.WorkHourBooking;
    if(this.purpose == 'slotBook') bookingType = BookingTypeValue.SliderBooking;
    const {
      localEndTimeValue,
      localStartTimeValue,
      subject,
      reminder,
      showAs,
      isOnlineMeeting,
      attendees,
      recurrenceStartDate,
      recurrenceEndDate,
      recurrenceType,
      repeatEvery,
      selectedWeekDays,
    } = this.scheduleMeetingInputValue;
    const { isOnPrem, emailAddress } = this.place || {
      isOnPrem: false,
      emailAddress: '',
    };
    this.weekdays.forEach(
      (weekday) =>
        weekday.active && selectedWeekDays.push(weekday.index.toString()),
    );
    return {
      bookingType: bookingType as BookingTypeValue,
      isOnPrem: isOnPrem,
      endTime: localEndTimeValue,
      startTime: localStartTimeValue,
      subject: !subject
        ? this.generateDefaultBookingSubject(recurrenceType)
        : subject,
      reminderMinutesBeforeStart: reminder,
      showAs: showAs,
      timeZoneInfo: Intl.DateTimeFormat().resolvedOptions().timeZone,
      isOnlineMeeting: isOnlineMeeting,
      // Quick bookings historically had no attendee picker. Authenticated room
      // checkout adds one so its participants and delegates must be retained.
      attendees:
        this.purpose !== 'book' || this.showCheckOutDelegateEditor
          ? stripCheckoutDelegateUiState(attendees)
          : [],
      placeEmailAddress: emailAddress,
      isAllDay: this.scheduleMeetingInputValue.isAllDay,
      recurrencePattern:
        recurrenceType == 'none' && !this.meetingToEdit?.editMasterEvent
          ? null
          : {
              startTime: moment(localStartTimeValue).toDate(),
              startDate: moment(recurrenceStartDate).utc(true).toDate(),
              endTime: moment(localEndTimeValue).toDate(),
              endDate: moment(recurrenceEndDate).utc(true).toDate(),
              interval: repeatEvery,
              accuranceDays: selectedWeekDays,
              accuranceType: recurrenceType,
              month: this.meetingToEdit?.recurrencePattern.month,
              index: this.meetingToEdit?.recurrencePattern.index,
              dayOfMonth:
                recurrenceType == 'AbsoluteMonthly'
                  ? moment(
                      this.scheduleMeetingInputValue.localStartTimeValue,
                    ).date()
                  : 0,
            },
    };
  }

  generateDefaultBookingSubject(recurrenceType) {
    switch (this.purpose) {
      case 'book':
        const quickBookSubject = 'HXA Quick Booking';
        return quickBookSubject;
      case 'plannedBook':
      case 'workingHours':
        const plannedBookSubject =
          recurrenceType == 'none'
            ? 'HXA Scheduled Booking'
            : 'HXA Series Booking';
        return plannedBookSubject;
      case 'slotBook':
        return 'HXA Scheduled Booking';
    }
  }

  validateInput() {
    const {
      localStartTimeValue,
      localEndTimeValue,
      recurrenceEndDate,
      recurrenceStartDate,
      selectedWeekDays,
      recurrenceType,
    } = this.scheduleMeetingInputValue;

    let startTime = moment(localStartTimeValue);
    let endTime = moment(localEndTimeValue);
    let recurrenceEnd = moment(recurrenceEndDate);
    let recurrenceStart = moment(recurrenceStartDate);
    if (recurrenceType != 'none') {
      if (recurrenceType == 'Weekly') {
        if (
          this.weekdays.filter((day) => day.active).length == 0 &&
          this.purpose == 'edit'
        ) {
          this.errorMessage = 'Please select at least one weekday.';
          this.showError = true;
          return false;
        }
      }
      if (recurrenceStart.isAfter(recurrenceEnd)) {
        this.errorMessage = 'Recurrence start date must be before end date.';
        this.showError = true;
        return false;
      }
    }
    if (startTime.isSameOrAfter(endTime)) {
      this.errorMessage = 'Start time must be before end time.';
      this.showError = true;
      return false;
    }
    const diffInMinutes = endTime.diff(startTime, 'minutes');
    if (diffInMinutes < 5) {
      this.errorMessage = this.translateService.instant('lessFiveMinutes');
      this.showError = true;
      return false;
    }
    if (this.purpose == 'slotBook' && this.slotMaxEndTime && endTime.isAfter(this.slotMaxEndTime)) {
      this.errorMessage = 'resourceAvailability.endTimeExceedsSlot';
      this.showError = true;
      return false;
    }
    this.errorMessage = '';
    this.showError = false;
    return true;
  }

  dismissModal() {
    this.modalController.dismiss();
  }

  handleTimeChange(
    event,
    type:
      | 'localStartTimeValue'
      | 'localEndTimeValue'
      | 'recurrenceEndDate'
      | 'recurrenceStartDate',
  ) {
    const oldDuration = this.duration;
    this.scheduleMeetingInputValue[type] = event.detail.value;
    if (type == 'localStartTimeValue') {
      const startTime = moment(
        this.scheduleMeetingInputValue.localStartTimeValue,
      );
      // they requested that if the booking is made by sparo using working hours booking,
      // then they dont want to change the end time when the start time is changed
      // we couldnt just return here because we need to calculate the duration again so we wrapped the logic inside this statement :/
      if (
        !(
          this.purpose == 'edit' &&
          this.meetingToEdit?.bookingType == BookingTypeValue.WorkHourBooking
        )
      ) {
        if (this.scheduleMeetingInputValue.isAllDay) {
          this.scheduleMeetingInputValue.localStartTimeValue = startTime
            .startOf('day')
            .format(DATETIMEFORMART);
          this.scheduleMeetingInputValue.localEndTimeValue = startTime
            .add(1, 'day')
            .startOf('day')
            .format(DATETIMEFORMART);
        } else {
          let newEndTime = startTime.clone().add(oldDuration);
          if (this.purpose == 'slotBook' && this.slotMaxEndTime && newEndTime.isAfter(this.slotMaxEndTime)) {
            newEndTime = moment(this.slotMaxEndTime);
          }
          this.scheduleMeetingInputValue.localEndTimeValue = newEndTime
            .format(DATETIMEFORMART);
        }
      }
    }

    if (this.purpose == 'slotBook' && this.slotMaxEndTime) {
      this.calculateDateTimeHours(
        this.scheduleMeetingInputValue.localStartTimeValue,
        this.slotMaxEndTime
      );
      this.calculateDateTimeMinutes(
        this.scheduleMeetingInputValue.localStartTimeValue,
        this.slotMaxEndTime
      );
    }

    this.calculateDuration(
      this.scheduleMeetingInputValue.localStartTimeValue,
      this.scheduleMeetingInputValue.localEndTimeValue,
    );
    if (this.meetingToEdit?.editMasterEvent)
      this.generateAndUpdateSeriesMessage();
  }

  handleAllDayToggle(event) {
    this.scheduleMeetingInputValue.isAllDay = event.detail.checked;
    if (this.scheduleMeetingInputValue.isAllDay) {
      this.scheduleMeetingInputValue.localStartTimeValue = moment(
        this.scheduleMeetingInputValue.localStartTimeValue,
      )
        .startOf('day')
        .format(DATETIMEFORMART);
      this.scheduleMeetingInputValue.localEndTimeValue = moment(
        this.scheduleMeetingInputValue.localStartTimeValue,
      )
        .add(1, 'day')
        .startOf('day')
        .format(DATETIMEFORMART);
    } else {
      let roundedStartTime = moment();
      const remainder = 5 - (roundedStartTime.minute() % 5);
      roundedStartTime = roundedStartTime.add(remainder, 'minutes');
      this.scheduleMeetingInputValue.localStartTimeValue =
        roundedStartTime.format(DATETIMEFORMART);
      this.scheduleMeetingInputValue.localEndTimeValue = moment(
        this.scheduleMeetingInputValue.localStartTimeValue,
      )
        .add(30, 'minutes')
        .format(DATETIMEFORMART);
    }
    this.calculateDuration(
      this.scheduleMeetingInputValue.localStartTimeValue,
      this.scheduleMeetingInputValue.localEndTimeValue,
    );
    this.generateAndUpdateSeriesMessage();
  }

  toggleWeekday(weekdayName) {
    this.weekdays.forEach((day) => {
      day.value == weekdayName && (day.active = !day.active);
    });
  }

  handleDurationChange(event) {
    this.checkIfLastHourWasChosen(event);
    const duration = event.detail.value;
    const durationMoment = moment.duration(duration);
    const startTime = moment(
      this.scheduleMeetingInputValue.localStartTimeValue,
    );
    this.scheduleMeetingInputValue.localEndTimeValue = moment(startTime)
      .clone()
      .add(durationMoment)
      .format(DATETIMEFORMART);
    this.duration = duration;
    if (this.meetingToEdit?.editMasterEvent)
      this.generateAndUpdateSeriesMessage();
  }

  setFocus(field) {
    field.setFocus();
  }

  async presentNotification(start: string, end: string) {
    let day = moment(start).locale(this.locale()).format('L');
    if (day == moment().locale(this.locale()).format('L')) {
      day = '';
    } else {
      day += ' ';
    }

    let message = '';
    if (this.purpose == 'book') {
      message =
        'HXA ' +
        this.translateService.instant('newBooking') +
        ' ' +
        day +
        this.translateService.instant('eventCreatedToastOne') +
        ' ' +
        moment(start).locale(this.locale()).format('HH:mm') +
        ' ' +
        this.translateService.instant('eventCreatedToastTwo') +
        ' ' +
        moment(end).locale(this.locale()).format('HH:mm') +
        ' ' +
        this.translateService.instant('eventCreatedToastThree');
    } else {
      message =
        'HXA ' +
        this.translateService.instant('scheduleBookingSubtitle') +
        ' ' +
        day +
        this.translateService.instant('eventCreatedToastOne') +
        ' ' +
        moment(start).locale(this.locale()).format('HH:mm') +
        ' ' +
        this.translateService.instant('eventCreatedToastTwo') +
        ' ' +
        moment(end).locale(this.locale()).format('HH:mm') +
        ' ' +
        this.translateService.instant('eventCreatedToastThree');
    }

    if (
      (this.platform.is('android') || this.platform.is('ios')) &&
      !this.platform.is('mobileweb')
    ) {
      if (this.purpose == 'slotBook') {
        const isNotificationPermissionExist =
          await this.localNotificationService.presentLocalNotification(
            message,
            'Booking status'
          );
        if (!isNotificationPermissionExist) {
          this.openToastIfSuccess(message);
        }
      }
    } else {
      this.openToastIfSuccess(message);
    }
    if (this.purpose != 'slotBook') {
      this.dismissModal();
    }
    this.bookingLoading = false;
    // this.searchService.resetDefaultSearchState();
    if (this.purpose != 'slotBook') {
      this.router.navigate(['tabs/bookings'], { replaceUrl: true });
    }
  }

  async openToastIfSuccess(message) {
    const toastOptions: any = {
      message: message,
      duration: 5000,
      position: 'top',
      swipeGesture: 'vertical',
    };
    if (this.purpose != 'slotBook') {
      toastOptions.positionAnchor = 'bookings-page-header-anchor';
    }
    await this.toastService.openActionToastAsync(toastOptions);
  }

  async createEventGeneralErrorHandler(customMessage?: string) {
    await this.toastService.openErrorToastAsync(
      customMessage || 'createEventGeneralErrorToast',
    );
  }

  // 3. Generates allowed hours list (Includes 24 for midnight starts)
  public calculateDateTimeHours(startTime: string, maxEndTime?: string) {
    this.maxAllowedDuration.hours = [];
    let startTimeToCompare = moment(startTime);

    // Check if it's a Microsoft All-Day event starting at midnight
    const isMidnightStart =
      startTimeToCompare.hours() === 0 && startTimeToCompare.minutes() === 0;

    if (isMidnightStart) {
      for (let index = 0; index <= 24; index++) {
        this.maxAllowedDuration.hours.push(index);
      }
      return;
    }

    let maxEnd = maxEndTime ? moment(maxEndTime) : moment(startTime).endOf('day');
    let differenceInMinutes = maxEnd.diff(startTimeToCompare, 'minutes');
    let differenceInHours = Math.floor(differenceInMinutes / 60);

    if (differenceInMinutes % 60 == 0) {
      differenceInHours -= 1;
    }
    for (let index = 0; index <= differenceInHours; index++) {
      this.maxAllowedDuration.hours.push(index);
    }
  }
  // calculate allowed minutes from start time
  // 4. Generates allowed minutes list (Includes 0-59 for midnight starts)
  public calculateDateTimeMinutes(startTime: string, maxEndTime?: string) {
    let startTimeToCompare = moment(startTime);
    const isMidnightStart =
      startTimeToCompare.hours() === 0 && startTimeToCompare.minutes() === 0;

    this.maxAllowedDuration.minutes = [];

    if (isMidnightStart) {
      for (let index = 0; index <= 59; index++) {
        this.maxAllowedDuration.minutes.push(index);
      }
      return;
    }

    let maxEnd = maxEndTime ? moment(maxEndTime) : moment(startTime).endOf('day');
    let differenceInMinutes = maxEnd.diff(startTimeToCompare, 'minutes') % 60;

    for (let index = 0; index <= differenceInMinutes; index++) {
      this.maxAllowedDuration.minutes.push(index);
    }
  }

  public checkIfLastHourWasChosen(event) {
    // modifying minutes on selecting max hour
    let duration = event.detail.value.split(':');
    let selectedHour = duration[0];
    if (
      selectedHour ==
      this.maxAllowedDuration.hours[this.maxAllowedDuration.hours.length - 1]
    ) {
      this.calculateDateTimeMinutes(
        this.scheduleMeetingInputValue.localStartTimeValue,
      );
    } else {
      // generate minutes 0..59
      this.maxAllowedDuration.minutes = Array.from({ length: 60 }, (_, i) => i);
    }
  }

  async openAttendeesModal() {
    const modal = await this.modalController.create({
      component: AddAttendeeComponent,
      initialBreakpoint: 0.95,
      breakpoints: [0, 0.95],
      canDismiss: async (data, role) => role != 'gesture',
      componentProps: {
        initialAttendees: this.scheduleMeetingInputValue.attendees,
        startTime: this.scheduleMeetingInputValue.localStartTimeValue,
        endTime: this.scheduleMeetingInputValue.localEndTimeValue,
        bookingType: this.purpose,
        showDelegateAction: this.showCheckOutDelegateEditor,
      },
    });

    modal.onDidDismiss().then((result) => {
      if (result.data) {
        this.scheduleMeetingInputValue.attendees =
          reconcileParticipantSelection(
            this.scheduleMeetingInputValue.attendees,
            result.data,
          );
      }
    });

    modal.present();
  }

  willPresent() {
    this.durationPickerVisible = true;
    Promise.resolve().then(() => {
      if (this.durationPicker) {
        this.durationPicker.value = this.duration;
      }
    });
  }
  didPresent() {
    setTimeout(() => {
      if (this.durationPicker) {
        this.durationPicker.value = this.duration;
      }
    }, 0);
  }
  didDismiss() {
    this.durationPickerVisible = false;
  }
  private initValuesForEditAndReadOnlyCase() {
    this.scheduleMeetingInputValue.localStartTimeValue = moment(
      this.meetingToEdit.startTime,
    ).format(DATETIMEFORMART);
    this.scheduleMeetingInputValue.localEndTimeValue = moment(
      this.meetingToEdit.endTime,
    ).format(DATETIMEFORMART);
    this.scheduleMeetingInputValue.showAs =
      this.meetingToEdit.showAs != 0
        ? this.meetingToEdit.showAs
        : this.scheduleMeetingInputValue.showAs; //if status is unknown we set default of user
    this.scheduleMeetingInputValue.subject = this.meetingToEdit.subject;
    this.scheduleMeetingInputValue.reminder =
      this.meetingToEdit.reminderMinutesBeforeStart;
    this.scheduleMeetingInputValue.attendees = (
      this.meetingToEdit.attendees ?? []
    ).map((attendee) => ({ ...attendee }));
    this.scheduleMeetingInputValue.isOnlineMeeting =
      this.meetingToEdit.isOnlineMeeting;
    if (this.scheduleMeetingInputValue.isOnlineMeeting)
      this.disableOnlineMeetingToggle = true;

    const endOfTheDay = moment(
      this.scheduleMeetingInputValue.localStartTimeValue,
    )
      .endOf('day')
      .format(DATETIMEFORMART);
    if (this.scheduleMeetingInputValue.localEndTimeValue == endOfTheDay) {
      this.scheduleMeetingInputValue.isAllDay = true;
    }
    if (this.meetingToEdit.editMasterEvent) {
      this.scheduleMeetingInputValue.recurrenceType =
        this.meetingToEdit.recurrencePattern.accuranceType;
      this.scheduleMeetingInputValue.repeatEvery =
        this.meetingToEdit.recurrencePattern.interval;
      this.scheduleMeetingInputValue.recurrenceStartDate = moment(
        this.meetingToEdit.recurrencePattern.startDate,
      ).format(DATETIMEFORMART);
      this.scheduleMeetingInputValue.recurrenceEndDate = moment(
        this.meetingToEdit.recurrencePattern.endDate,
      ).format(DATETIMEFORMART);

      if (this.meetingToEdit.recurrencePattern.accuranceType == 'Weekly') {
        this.weekdays.forEach((weekDay) => {
          if (
            this.meetingToEdit.recurrencePattern.accuranceDays.includes(
              weekDay.index,
            )
          ) {
            weekDay.active = true;
          }
        });
      }
      this.generateAndUpdateSeriesMessage(false);
    }
    // Generate map URL for read-only bookings (not just for master events)
    if (
      this.purpose == 'readOnlyBooking' &&
      !this.meetingToEdit?.isResourcelessEvent
    ) {
      this.generateResourceMapUrl();
    }

    // Initialize selectedResponse from the server's current response so it shows highlighted on reopen
    if (this.purpose == 'readOnlyBooking') {
      const currentUserEmail = this.userStore.user()?.mail;
      const userAttendee = this.meetingToEdit.attendees?.find(
        (a) => a.userPrincipalName === currentUserEmail,
      );
      if (userAttendee) {
        const response = userAttendee.response?.toLowerCase();
        if (response === 'accepted') this.selectedResponse = 'accept';
        else if (response === 'tentativelyaccepted')
          this.selectedResponse = 'tentativelyAccept';
        else if (response === 'declined') this.selectedResponse = 'decline';
        else this.selectedResponse = null;
      }
    }
  }

  private generateResourceMapUrl() {
    if (!this.place || !this.place.address) {
      this.resourceMapUrl = '';
    } else {
      const addressString = this.stringInfoMessageService.buildAddressString(
        this.place,
      );
      if (!addressString || addressString.trim() === '') {
        this.resourceMapUrl = '';
      } else {
        this.resourceMapUrl = `https://maps.google.com/maps?q=${encodeURIComponent(addressString)}&z=15&output=embed`;
      }
    }
  }
  convertEventFromReadOnlyToEdit() {
    this.purpose = 'edit';
    this.initValues(this.purpose);
  }

  get isUnrespondedInvitation(): boolean {
    if (
      !this.meetingToEdit ||
      this.meetingToEdit.isOrganizer ||
      this.meetingToEdit.isCancelled
    ) {
      return false;
    }
    const currentUserEmail = this.userStore.user()?.mail;
    if (!currentUserEmail) {
      return false;
    }
    const userAttendee = this.meetingToEdit.attendees?.find(
      (attendee) => attendee.userPrincipalName === currentUserEmail,
    );
    if (!userAttendee) {
      return false;
    }
    const response = userAttendee.response?.toLowerCase();
    return response === 'none' || response === 'notresponded';
  }

  get isInvitation(): boolean {
    if (
      !this.meetingToEdit ||
      this.meetingToEdit.isOrganizer ||
      this.meetingToEdit.isCancelled
    ) {
      return false;
    }
    const currentUserEmail = this.userStore.user()?.mail;
    if (!currentUserEmail) {
      return false;
    }
    if (
      this.meetingToEdit.attendees?.some(
        (a) => a.userPrincipalName === currentUserEmail,
      )
    ) {
      return true;
    }
    return false;
  }

  async acceptInvitation() {
    const eventId = this.meetingToEdit?.seriesMasterId
      ? await this.chooseSeriesOrOccurrence('accept')
      : this.meetingToEdit.id;

    if (!eventId) return;

    const isSeries = eventId === this.meetingToEdit?.seriesMasterId;
    await this.confirmAndAccept(eventId, isSeries);
  }

  selectResponse(response: 'accept' | 'tentativelyAccept' | 'decline') {
    if (this.selectedResponse === response) return;
    this.selectedResponse = response;
    this.confirmSelectedResponse();
  }

  async confirmSelectedResponse() {
    if (!this.selectedResponse || !this.meetingToEdit?.id) return;

    const isSeries = this.meetingToEdit?.seriesMasterId ? true : false;
    if (isSeries) {
      switch (this.selectedResponse) {
        case 'accept':
          await this.acceptInvitation();
          break;
        case 'tentativelyAccept':
          await this.tentativelyAcceptInvitation();
          break;
        case 'decline':
          await this.declineInvitation();
          break;
      }
    } else {
      this.responseLoading = true;

      switch (this.selectedResponse) {
        case 'accept':
          this.bookingsStore
            .acceptInvitation(this.meetingToEdit.id, false)
            .subscribe({
              next: () => {
                this.responseLoading = false;
                this.dismissModal();
              },
              error: (ex) => {
                this.responseLoading = false;
                this.toastService.openErrorToastAsync(
                  ex.error?.message || 'createEventGeneralErrorToast',
                );
              },
            });
          break;
        case 'tentativelyAccept':
          this.bookingsStore
            .tentativelyAcceptInvitation(this.meetingToEdit.id, false)
            .subscribe({
              next: () => {
                this.responseLoading = false;
                this.dismissModal();
              },
              error: (ex) => {
                this.responseLoading = false;
                this.toastService.openErrorToastAsync(
                  ex.error?.message || 'createEventGeneralErrorToast',
                );
              },
            });
          break;
        case 'decline':
          this.bookingsStore
            .declineInvitation(this.meetingToEdit.id, false)
            .subscribe({
              next: () => {
                this.responseLoading = false;
                this.modalController.dismiss({
                  action: 'declined',
                  eventId: this.meetingToEdit.id,
                });
              },
              error: (ex) => {
                this.responseLoading = false;
                this.toastService.openErrorToastAsync(
                  ex.error?.message || 'createEventGeneralErrorToast',
                );
              },
            });
          break;
      }
    }
  }

  async tentativelyAcceptInvitation() {
    const eventId = this.meetingToEdit?.seriesMasterId
      ? await this.chooseSeriesOrOccurrence('tentativelyAccept')
      : this.meetingToEdit.id;

    if (!eventId) return;

    const isSeries = eventId === this.meetingToEdit?.seriesMasterId;
    await this.confirmAndTentativelyAccept(eventId, isSeries);
  }

  async declineInvitation() {
    const eventId = this.meetingToEdit?.seriesMasterId
      ? await this.chooseSeriesOrOccurrence('decline')
      : this.meetingToEdit.id;

    if (!eventId) return;

    const isSeries = eventId === this.meetingToEdit?.seriesMasterId;
    await this.confirmAndDecline(eventId, isSeries);
  }

  private async chooseSeriesOrOccurrence(type: string): Promise<string | null> {
    const actionSheet = await this.actionSheetController.create({
      header: this.translateService.instant(
        'series.' + type + 'SeriesEventHeaderModal',
      ),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('series.EventOccurrence'),
          role: 'occurrence',
        },
        {
          text: this.translateService.instant('series.EventSeries'),
          role: 'series',
        },
        {
          text: this.translateService.instant('cancelButton'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const result = await actionSheet.onDidDismiss();

    if (result.role === 'occurrence') {
      return this.meetingToEdit.id;
    } else if (result.role === 'series') {
      return this.meetingToEdit.seriesMasterId;
    }
    return null;
  }

  private async confirmAndAccept(eventId: string, isSeries: boolean) {
    const actionSheet = await this.actionSheetController.create({
      header: isSeries
        ? this.translateService.instant('acceptSeriesMeetingQuestion')
        : this.translateService.instant('acceptMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.responseLoading = true;
      this.bookingsStore.acceptInvitation(eventId, isSeries).subscribe({
        next: () => {
          this.responseLoading = false;
          this.dismissModal();
        },
        error: (ex) => {
          this.responseLoading = false;
          this.toastService.openErrorToastAsync(
            ex.error?.message || 'createEventGeneralErrorToast',
          );
        },
      });
    }
  }

  private async confirmAndTentativelyAccept(
    eventId: string,
    isSeries: boolean,
  ) {
    const actionSheet = await this.actionSheetController.create({
      header: isSeries
        ? this.translateService.instant(
            'tentativelyAcceptSeriesMeetingQuestion',
          )
        : this.translateService.instant('tentativelyAcceptMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.responseLoading = true;
      this.bookingsStore
        .tentativelyAcceptInvitation(eventId, isSeries)
        .subscribe({
          next: () => {
            this.responseLoading = false;
            this.dismissModal();
          },
          error: (ex) => {
            this.responseLoading = false;
            this.toastService.openErrorToastAsync(
              ex.error?.message || 'createEventGeneralErrorToast',
            );
          },
        });
    }
  }

  private async confirmAndDecline(eventId: string, isSeries: boolean) {
    const actionSheet = await this.actionSheetController.create({
      header: isSeries
        ? this.translateService.instant('declineSeriesMeetingQuestion')
        : this.translateService.instant('declineMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.responseLoading = true;
      this.bookingsStore.declineInvitation(eventId, isSeries).subscribe({
        next: () => {
          this.responseLoading = false;
          this.modalController.dismiss({
            action: 'declined',
            eventId: eventId,
          });
        },
        error: (ex) => {
          this.responseLoading = false;
          this.toastService.openErrorToastAsync(
            ex.error?.message || 'createEventGeneralErrorToast',
          );
        },
      });
    }
  }
}
